import React, { FC, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Spacer } from 'components/Utility'
import { Description, MainHeadline, SubDescription } from 'components/Typography'
import { NavButton, OrangeButton } from 'components/Button'
import { Checkbox, SelectBox, TextInput } from 'components/Form'
import { useCheckRef, useSelectNumber, useStringChangeEvent } from 'hooks/form'
import { FormLabel } from 'components/Label'
import typography from 'styles/components/typography.module.scss'
import { ExternalLink } from 'components/Link'
import { staffRoleOptions } from 'constants/options'
import { ManagedStoreType } from 'types/store'
import { useManagedStores } from 'hooks/store'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getIsAdmin, getStoreRef, getSwitchingStoreRefs } from 'reducks/auth/selectors'
import { getStores } from 'reducks/store/selectors'
import { fetchStores } from 'reducks/store/operations'
import { db, FirebaseDocRefType } from 'firebase/index'
import { getEmailError } from 'reducks/error/selectors'
import { isValidEmailFormat } from 'reducks/error/operations'
import { fetchStaffs, inviteStaff } from 'reducks/staff/operations'
import { getStaffs } from 'reducks/staff/selectors'

const InviteMemberTemplate: FC = () => {
  const dispatch = useDispatch()
  const isAdmin = useSelector((state: State) => getIsAdmin(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const emailError = useSelector((state: State) => getEmailError(state))
  const switchingStoreRefs = useSelector((state: State) => getSwitchingStoreRefs(state))
  const staffs = useSelector((state: State) => getStaffs(state))
  const stores = useSelector((state: State) => getStores(state))

  const [email, setEmail] = useState<string>('')
  const [role, setRole] = useState<number>(0)
  const [storeRefs, setStoreRefs] = useState<FirebaseDocRefType[]>([])

  const handleEmail = useStringChangeEvent(setEmail)
  const handleStoreRefs = useCheckRef(setStoreRefs, db.collection('stores'))
  const selectRole = useSelectNumber(setRole)

  const isAdminViewingStore = isAdmin && typeof storeRef !== 'undefined'
  const managedStores: ManagedStoreType[] = useManagedStores(switchingStoreRefs, stores, isAdminViewingStore, staffs)

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStaffs(storeRef, switchingStoreRefs))
  }, [storeRef, switchingStoreRefs])

  useEffect(() => {
    dispatch(fetchStores(true))
  }, [])

  // アクセス中の店舗にデフォルトでチェックを入れる
  useEffect(() => {
    if (!storeRef) return
    setStoreRefs([storeRef])
  }, [storeRef])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'メンバーを招待する'} />
      <Spacer size={28} />
      <Description
        description={'管理画面へのアクセスを許可する人のメールアドレスを入力してください。招待リンクは7日間有効です。'}
      />
      <Spacer size={48} />
      <TextInput
        disabled={false}
        error={emailError}
        label={'メールアドレス'}
        onBlur={() => dispatch(isValidEmailFormat(email))}
        onChange={handleEmail}
        required={true}
        type={'email'}
        value={email}
      />
      <Spacer size={16} />
      <FormLabel label={'権限'} required={true} />
      <Spacer size={8} />
      <p className={typography.description_sub}>
        管理者は全ての操作を行うことができます。ビューワーは、お客さまの注文確認や在庫変更ができますが、新規の商品登録はできません。詳しくは
        <ExternalLink label={'こちらの記事'} url={'https://sacrisupport.zendesk.com/hc/ja/articles/4403860286745'} />
        を参考にしてください。
      </p>
      <Spacer size={8} />
      <SelectBox options={staffRoleOptions} select={selectRole} value={role} />
      <Spacer size={16} />
      <FormLabel label={'アクセスできる店舗を選択する'} required={true} />
      <Spacer size={8} />
      <SubDescription description={'アクセスを許可する店舗にチェックを入れてください。'} />
      <Spacer size={8} />
      {managedStores.map((managedStore) => {
        return (
          <React.Fragment key={managedStore.store_ref.id}>
            <Checkbox
              id={managedStore.store_ref.id}
              isChecked={storeRefs.some((storeRef) => storeRef.id === managedStore.store_ref.id)}
              label={managedStore.store_name}
              onChange={handleStoreRefs}
            />
            <Spacer size={8} />
          </React.Fragment>
        )
      })}
      <Spacer size={48} />
      <OrangeButton
        disabled={email === '' || role === 0 || storeRefs.length === 0}
        label={'送信する'}
        onClick={() => dispatch(inviteStaff(email, role, storeRefs, false))}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={24} />
      <NavButton label={'店舗アカウント設定に戻る'} iconPath={'/icon/grey/arrow_prev.svg'} url={'/account'} />
      <Spacer size={12} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
    </div>
  )
}

export default InviteMemberTemplate
